import { GiftCardType } from '@mixtiles/web-backend-shared'
import sumBy from 'lodash/sumBy'
import { type GiftCardBalance } from '../../pages/GiftCard/GiftCard.types'
import {
  type GiftCardCreditsSummaryItem,
  type ProductSummaryItem,
} from '../../components/Pricing/types/checkout.types'
import { convertPriceToUSDCurrency } from '../../utils/utils'

export function getGiftCardTypeForAnalytics(
  giftCardType: GiftCardType
): string {
  // Map gift card type to "Quantity" or "Value"
  return giftCardType === GiftCardType.VALUE ? 'Value' : 'Quantity'
}

type PriceSummary = {
  giftsBalances?: GiftCardBalance[]
  giftCardCreditItems?: GiftCardCreditsSummaryItem[]
}

type FreeTilesData = {
  freeTilesCreditUsed: number
}

type OrderPriceSummary = {
  products?: ProductSummaryItem[]
  freeTilesData?: FreeTilesData
  currency?: string
}

export function getGiftCardProps(
  priceSummary: PriceSummary,
  giftCardType?: GiftCardType
) {
  const { giftsBalances, giftCardCreditItems } = priceSummary

  const baseProps =
    !giftsBalances || giftsBalances.length === 0
      ? {}
      : {
          'Gift Card IDs': giftsBalances.map((giftBalance) => giftBalance.code),
          'Gift Card Count': giftsBalances.length,
          'Gift Card Credits Balance': sumBy(
            giftsBalances,
            (i) => i.balance ?? 0
          ),
          'Gift Card Credits Redeemed': Math.abs(
            sumBy(giftCardCreditItems, (i) => i.price)
          ),
        }

  // Add gift card type property if giftCardType is provided
  if (giftCardType) {
    return {
      ...baseProps,
      'Gift Card Type': getGiftCardTypeForAnalytics(giftCardType),
    }
  }

  return baseProps
}

export function getGiftCardRedeemedProps(orderPriceSummary: OrderPriceSummary) {
  if (!orderPriceSummary?.products || orderPriceSummary.products.length === 0) {
    return {
      'Is Gift Card Applied': false,
      'Gift Card Redeemed USD Value': 0,
    }
  }

  const redeemedGiftcardValue = orderPriceSummary.products
    .map((p) => p.priceBeforeDiscount - p.priceAfterDiscount)
    .reduce((a, b) => a + b, 0)

  const currency = orderPriceSummary.currency || 'USD'

  return {
    'Is Gift Card Applied': redeemedGiftcardValue > 0,
    'Gift Card Redeemed USD Value': convertPriceToUSDCurrency(
      redeemedGiftcardValue,
      currency
    ),
    'Gift Card Type':
      redeemedGiftcardValue === 0
        ? undefined
        : (orderPriceSummary.freeTilesData?.freeTilesCreditUsed ?? 0) > 0
        ? 'Quantity'
        : 'Value',
  }
}
